import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useEngagedPatients } from '../hooks/useEngagedPatients';
import { usePatients } from '../hooks/usePatients';
import PatientDrawer from '../components/patients/PatientDrawer';
import SendMessageDrawer from '../components/patients/SendMessageDrawer';
import { Button } from '../components/shadcn/button';
import { Input } from '../components/shadcn/input';
import {
  Search,
  Eye,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { normalizeStatus } from '../utils/statuses';
import { formatPhone } from '../utils/formatters';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

// Stat Card
const StatCard = ({ value, label, icon: Icon, accent }) => {
  const accentStyles = {
    default: 'text-slate-900',
    purple: 'text-purple-600',
    warning: 'text-orange-500',
    muted: 'text-slate-500',
  };

  return (
    <div className="bg-white rounded-lg p-5 shadow-sm border border-slate-200">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">
            {label}
          </p>
          <p className={`text-3xl font-bold tracking-tight ${accentStyles[accent] || accentStyles.default}`}>
            {value}
          </p>
        </div>
        {Icon && (
          <Icon className={`h-5 w-5 ${accent === 'purple' ? 'text-purple-500' : accent === 'warning' ? 'text-orange-500' : 'text-slate-400'}`} strokeWidth={1.5} />
        )}
      </div>
    </div>
  );
};

// Patient Row
const PatientRow = ({ patient, onClick, onMessage }) => {
  const statusStyle = (() => {
    const ns = normalizeStatus(patient.status);
    if (ns === 'contacted') return { text: 'text-emerald-600', dot: 'bg-emerald-500' };
    if (ns === 'follow_up') return { text: 'text-amber-600', dot: 'bg-amber-500' };
    if (ns === 'recovered') return { text: 'text-green-600', dot: 'bg-green-500' };
    if (ns === 'closed') return { text: 'text-gray-500', dot: 'bg-gray-400' };
    return { text: 'text-slate-500', dot: 'bg-slate-300' };
  })();

  return (
    <div
      onClick={onClick}
      className="group flex items-center gap-4 px-5 py-4 cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
    >
      {/* Patient Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-slate-900 truncate">{patient.name}</p>
          {patient.needsNudge && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700">
              <AlertCircle className="h-3 w-3" />
              Needs follow-up
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          {patient.phone && (
            <span className="text-xs text-slate-400">{formatPhone(patient.phone)}</span>
          )}
          {patient.email && (
            <span className="text-xs text-slate-400 truncate">{patient.email}</span>
          )}
        </div>
      </div>

      {/* Engagement Info */}
      <div className="hidden sm:flex flex-col items-end gap-0.5">
        <span className="text-xs text-purple-600 font-medium">
          Engaged {patient.daysSinceEngagement === 0 ? 'today' : `${patient.daysSinceEngagement}d ago`}
        </span>
        <span className="text-[10px] text-slate-400">
          {dayjs(patient.engagedAt).format('MMM D, h:mm A')}
        </span>
      </div>

      {/* Status */}
      <div className="hidden md:flex items-center gap-1.5 w-24">
        <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot}`} />
        <span className={`text-xs ${statusStyle.text}`}>
          {patient.status || 'New'}
        </span>
      </div>

      {/* Action */}
      <div onClick={(e) => e.stopPropagation()}>
        <Button
          size="sm"
          onClick={onMessage}
          className="opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 hover:bg-slate-800 text-white text-xs"
        >
          Message
        </Button>
      </div>
    </div>
  );
};

const PatientsEngaged = () => {
  const { engagedNotBookedPatients, loading, stats, refetch } = useEngagedPatients();
  const { updatePatient } = usePatients();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMessageDrawerOpen, setIsMessageDrawerOpen] = useState(false);

  // Filter by time threshold
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', '48h', '7d'

  // Filter patients
  const filteredPatients = React.useMemo(() => {
    let filtered = engagedNotBookedPatients;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query) ||
        p.phone?.includes(query)
      );
    }

    // Time threshold filter
    if (timeFilter === '48h') {
      filtered = filtered.filter(p => p.needsNudge);
    } else if (timeFilter === '7d') {
      filtered = filtered.filter(p => p.daysSinceEngagement >= 7);
    }

    return filtered;
  }, [engagedNotBookedPatients, searchQuery, timeFilter]);

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    setIsDrawerOpen(true);
  };

  const handleSendMessage = (patient, e) => {
    e?.stopPropagation();
    setSelectedPatient(patient);
    setIsMessageDrawerOpen(true);
  };

  const handlePatientUpdate = async (id, updates) => {
    setSelectedPatient(prev => prev?.id === id ? { ...prev, ...updates } : prev);
    await updatePatient(id, updates);
    refetch();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Sub-navigation tabs */}
        <div className="flex items-center gap-1 border-b border-slate-200">
          <Link
            to="/patients"
            className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-700 border-b-2 border-transparent hover:border-slate-300 transition-colors"
          >
            Triage
          </Link>
          <Link
            to="/patients/engaged"
            className="px-4 py-2 text-sm font-medium text-slate-900 border-b-2 border-slate-900 flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            Engaged - Not Booked
            {stats.needsNudge > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-purple-100 text-purple-700 rounded-full">
                {stats.needsNudge}
              </span>
            )}
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            value={stats.total}
            label="Total Engaged"
            icon={Eye}
            accent="purple"
          />
          <StatCard
            value={stats.needsNudge}
            label="48+ Hours"
            icon={Clock}
            accent="warning"
          />
          <StatCard
            value={`${stats.avgDaysSinceEngagement}d`}
            label="Avg Wait Time"
            icon={AlertCircle}
            accent="muted"
          />
        </div>

        {/* Search and Filters */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" strokeWidth={1.5} />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 bg-white border-slate-200 text-sm placeholder:text-slate-400 focus:ring-1 focus:ring-slate-300 focus:border-slate-300"
            />
          </div>

          {/* Time Filter */}
          <div className="flex gap-1 bg-white rounded-lg border border-slate-200 p-1">
            {[
              { value: 'all', label: 'All' },
              { value: '48h', label: '48h+' },
              { value: '7d', label: '7d+' },
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setTimeFilter(option.value)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  timeFilter === option.value
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Patient List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <div className="w-6 h-6 border-2 border-slate-200 border-t-slate-500 rounded-full animate-spin mb-3" />
            <p className="text-xs">Loading...</p>
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg border border-slate-200">
            <Eye className="h-8 w-8 text-slate-300 mb-3" strokeWidth={1.5} />
            <h3 className="text-sm font-medium text-slate-900">No engaged patients</h3>
            <p className="text-xs text-slate-500 mt-1">
              {searchQuery || timeFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Patients who read your messages will appear here'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">
                {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''}
              </span>
              <span className="text-xs text-slate-400">
                Sorted by engagement time (oldest first)
              </span>
            </div>
            {filteredPatients.map(patient => (
              <PatientRow
                key={patient.id}
                patient={patient}
                onClick={() => handlePatientClick(patient)}
                onMessage={(e) => handleSendMessage(patient, e)}
              />
            ))}
          </div>
        )}

        {/* Drawers */}
        <PatientDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          patient={selectedPatient}
          onPatientUpdate={handlePatientUpdate}
        />

        <SendMessageDrawer
          isOpen={isMessageDrawerOpen}
          onClose={() => setIsMessageDrawerOpen(false)}
          patient={selectedPatient}
        />
      </div>
    </div>
  );
};

export default PatientsEngaged;
